import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfileContext } from '../hooks/useProfileContext';
import { canEditLayoutFor, canEditLayoutsFor, type LayoutScope } from '../lib/layoutAccess';
import LayoutAccessDenied from './LayoutAccessDenied';

/**
 * PAGE guard of the 3 PDF layout routes (/band/:bandSlug/rider-layout,
 * /band/:bandSlug/daysheet-layout, /tour/tourbook-layout).
 *
 * Before this, the routes were open to any signed-in user: the sidebar hid the
 * links, but typing the URL opened the editor and only the API refused the
 * WRITE (403 at save time). The gate is now on the page itself.
 *
 * `permission` narrows the guard to ONE capability (chantier C, 2026-09-25):
 * a member granted only `layout.daysheet` must not open — nor write — the rider
 * layout, exactly like the server (`canWriteLayout(req, bandId, which)`).
 * Omitted, the guard accepts any `layout.*` capability (backward compatible).
 */
export default function RequireLayoutAccess({ children, permission }: { children: ReactNode; permission?: LayoutScope }) {
  const { user } = useAuth();
  const { activeProfileId, getProfile } = useProfileContext();
  const { bandSlug } = useParams<{ bandSlug?: string }>();
  // Same expression as the sidebar section in App.tsx (single source of truth).
  const isSuperAdmin = user?.global_role === 'superadmin';
  const isShowcase = !!user?.is_showcase;
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const bandRole = isSuperAdmin ? 'owner' : activeProfile?.my_role;
  const input = {
    isSuperAdmin,
    isShowcase,
    bandRole,
    permissions: activeProfile?.permissions,
    layoutAccess: activeProfile?.layout_access,
  };
  const allowed = permission
    ? canEditLayoutFor(input, permission)
    : canEditLayoutsFor(input);

  // A direct URL open of /band/:slug/... : AppLayout resolves the slug into the
  // active band in an effect AFTER the first render. Stay neutral while it
  // settles instead of flashing "no access" at a user who does have the right.
  const bandPending = !!bandSlug
    && (!activeProfile || (activeProfile.slug !== bandSlug && activeProfile.id !== bandSlug));
  if (!allowed && bandPending) return <div className="page" />;

  if (!allowed) return <LayoutAccessDenied titleKey="layoutEditor.noAccessTitle" />;
  return <>{children}</>;
}